// src/pages/affiliate/HelpPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/AffiliateHelp.css';

const AffiliateHelpPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const faqSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'fas fa-rocket',
      questions: [
        {
          q: 'How do I get my referral link?',
          a: 'Your referral link is automatically generated when you sign up. You can find it in your dashboard under "Marketing Materials". You can also customize it with UTM parameters for better tracking.'
        },
        {
          q: 'How do I track my referrals?',
          a: 'All referrals are automatically tracked when someone clicks your referral link and makes a booking. You can view your performance in the Dashboard and Detailed Analytics sections.'
        },
        {
          q: 'When do I get paid?',
          a: 'Commissions are typically paid out monthly, after a 30-day grace period to ensure bookings are completed. You can request a payout once you reach the minimum payout threshold ($50).'
        }
      ]
    },
    {
      id: 'commissions',
      title: 'Commissions & Earnings',
      icon: 'fas fa-dollar-sign',
      questions: [
        {
          q: 'How are commissions calculated?',
          a: 'Commissions are calculated as a percentage of the total booking amount. Your commission rate depends on your tier (Bronze: 10%, Silver: 15%, Gold: 20%, Platinum: 25%).'
        },
        {
          q: 'What is the grace period?',
          a: 'The grace period is a 30-day window after a booking is made. During this time, the commission status is "pending". If the booking is completed successfully, the commission moves to "approved" status.'
        },
        {
          q: 'What happens if a booking is cancelled?',
          a: 'If a booking is cancelled within the grace period, the associated commission will be marked as "cancelled" and you will not receive payment for it.'
        },
        {
          q: 'How do I request a payout?',
          a: 'Add your PayPal email on the Profile page, then go to Payouts and click "Request Payout". You must have at least $50 in available balance. Referral agent payouts are sent to PayPal only.'
        }
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing & Promotion',
      icon: 'fas fa-bullhorn',
      questions: [
        {
          q: 'What marketing materials are available?',
          a: 'We provide referral links, QR codes, banners, social media templates, and email templates. All materials are available in the Marketing Materials section.'
        },
        {
          q: 'Can I customize my referral link?',
          a: 'Yes! You can add UTM parameters to your referral link to track different campaigns. For example: ?utm_source=instagram&utm_campaign=summer2024'
        },
        {
          q: 'How do I use the QR code?',
          a: 'Download your QR code from the Marketing Materials page. You can print it, share it digitally, or include it in your social media posts. When scanned, it will direct users to your referral link.'
        },
        {
          q: 'Are there any promotional guidelines?',
          a: 'Yes. Please ensure all promotional content is accurate, complies with our brand guidelines, and follows applicable advertising regulations. Do not make false claims or misrepresent our services.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Account & Profile',
      icon: 'fas fa-user',
      questions: [
        {
          q: 'How do I update my profile?',
          a: 'Go to the Edit Profile page from your dashboard. You can update your personal information, address, and social media details. Note: Email cannot be changed - contact support if needed.'
        },
        {
          q: 'How do I change my payout method?',
          a: 'Go to your Profile page and click "Update PayPal Email" in the Payment Method section. Referral agent payouts are sent to that PayPal email only.'
        },
        {
          q: 'What are referral agent tiers?',
          a: 'Tiers are based on your performance. As you earn more and refer more customers, you can move up tiers (Bronze → Silver → Gold → Platinum) and earn higher commission rates.'
        },
        {
          q: 'What if my account is suspended?',
          a: 'If your account is suspended, you will receive an email with the reason. Contact support immediately to resolve the issue. Common reasons include policy violations or suspicious activity.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Support',
      icon: 'fas fa-tools',
      questions: [
        {
          q: 'My referral link is not working',
          a: 'First, check that you\'re using the correct link from your Marketing Materials page. If it still doesn\'t work, clear your browser cache and try again. Contact support if the issue persists.'
        },
        {
          q: 'I\'m not seeing clicks/conversions tracked',
          a: 'Make sure you\'re using your unique referral link. Clicks are tracked via cookies, so users must click your link before making a booking. Check your Analytics page for detailed tracking information.'
        },
        {
          q: 'How do I contact support?',
          a: 'You can contact our referral agent support team by emailing affiliates@lookbel.com or using the contact form below. We typically respond within 24-48 hours.'
        }
      ]
    }
  ];

  const toggleSection = (sectionId: string) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  return (
    <div className="affiliate-help">
      <div className="affiliate-help__header">
        <div>
          <h1>Help & Support</h1>
          <p>Find answers to common questions and get help when you need it</p>
        </div>
        <button onClick={() => navigate('/affiliate/dashboard')} className="btn-secondary">
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      {/* Quick Links */}
      <div className="help-quick-links">
        <div className="quick-link-card" onClick={() => navigate('/affiliate/marketing-materials')}>
          <i className="fas fa-images"></i>
          <h3>Marketing Materials</h3>
          <p>Get your referral links, QR codes, and promotional materials</p>
        </div>
        <div className="quick-link-card" onClick={() => navigate('/affiliate/analytics')}>
          <i className="fas fa-chart-line"></i>
          <h3>Analytics</h3>
          <p>View detailed performance metrics and insights</p>
        </div>
        <div className="quick-link-card" onClick={() => navigate('/affiliate/commissions')}>
          <i className="fas fa-dollar-sign"></i>
          <h3>Commissions</h3>
          <p>Track your earnings and commission history</p>
        </div>
        <div className="quick-link-card" onClick={() => navigate('/affiliate/payouts')}>
          <i className="fas fa-wallet"></i>
          <h3>Payouts</h3>
          <p>Request payouts and manage payment methods</p>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="help-faq">
        <h2>Frequently Asked Questions</h2>
        {faqSections.map((section) => (
          <div key={section.id} className="faq-section">
            <div
              className="faq-section-header"
              onClick={() => toggleSection(section.id)}
            >
              <div className="faq-section-title">
                <i className={section.icon}></i>
                <h3>{section.title}</h3>
              </div>
              <i
                className={`fas fa-chevron-${activeSection === section.id ? 'up' : 'down'}`}
              ></i>
            </div>
            {activeSection === section.id && (
              <div className="faq-questions">
                {section.questions.map((item, index) => (
                  <div key={index} className="faq-item">
                    <h4>{item.q}</h4>
                    <p>{item.a}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Contact Support */}
      <div className="help-contact">
        <h2>Still Need Help?</h2>
        <p>Our referral agent support team is here to help you succeed</p>
        <div className="contact-methods">
          <div className="contact-method">
            <i className="fas fa-envelope"></i>
            <h3>Email Support</h3>
            <p>affiliates@lookbel.com</p>
            <a href="mailto:affiliates@lookbel.com" className="btn-primary">
              Send Email
            </a>
          </div>
          <div className="contact-method">
            <i className="fas fa-clock"></i>
            <h3>Response Time</h3>
            <p>We typically respond within 24-48 hours</p>
          </div>
          <div className="contact-method">
            <i className="fas fa-headset"></i>
            <h3>Business Hours</h3>
            <p>Monday - Friday, 9 AM - 6 PM EST</p>
          </div>
        </div>
      </div>

      {/* Resources */}
      <div className="help-resources">
        <h2>Additional Resources</h2>
        <div className="resources-grid">
          <div className="resource-card">
            <i className="fas fa-book"></i>
            <h3>Referral Program Guide</h3>
            <p>Complete guide to maximizing your referral earnings</p>
            <span className="btn-link">Read Guide →</span>
          </div>
          <div className="resource-card">
            <i className="fas fa-video"></i>
            <h3>Video Tutorials</h3>
            <p>Step-by-step video guides for common tasks</p>
            <span className="btn-link">Watch Videos →</span>
          </div>
          <div className="resource-card">
            <i className="fas fa-users"></i>
            <h3>Referral Community</h3>
            <p>Connect with other referral agents and share tips</p>
            <span className="btn-link">Join Community →</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateHelpPage;

